package com.gloopgo.game.domain

import com.gloopgo.game.engine.GameConstants
import com.gloopgo.game.engine.GloopCell

sealed interface PredationDecision {
    data class Consumed(
        val eaterPlayerId: String,
        val eaterCellId: String,
        val victimPlayerId: String,
        val victimCellId: String,
        val massGained: Float
    ) : PredationDecision

    object NoCollision : PredationDecision
}

/**
 * Pure domain logic for cell-vs-cell predation evaluation.
 */
object PredationLogic {

    /**
     * Evaluates whether one cell can consume another.
     *
     * Requirements:
     * 1. Mass advantage: Predator cell must have at least 15% more mass (`mass >= 1.15 * prey.mass`).
     * 2. Border touch: Distance between centers must be less than or equal to `predator.radius + prey.radius`
     *    (impact occurs as soon as the blob touches the border of the other player, not waiting to hit the center).
     */
    fun evaluate(
        predatorPlayerId: String,
        predatorCell: GloopCell,
        preyPlayerId: String,
        preyCell: GloopCell
    ): PredationDecision {
        if (predatorPlayerId == preyPlayerId) return PredationDecision.NoCollision

        val dist = predatorCell.position.distanceTo(preyCell.position)
        val touchDist = predatorCell.radius + preyCell.radius

        // Check if predator can eat prey
        if (predatorCell.mass >= preyCell.mass * GameConstants.EAT_MASS_ADVANTAGE) {
            if (dist <= touchDist) {
                return PredationDecision.Consumed(
                    eaterPlayerId = predatorPlayerId,
                    eaterCellId = predatorCell.id,
                    victimPlayerId = preyPlayerId,
                    victimCellId = preyCell.id,
                    massGained = preyCell.mass
                )
            }
        }

        // Check if prey can eat predator (if caller passed them in reverse)
        if (preyCell.mass >= predatorCell.mass * GameConstants.EAT_MASS_ADVANTAGE) {
            if (dist <= touchDist) {
                return PredationDecision.Consumed(
                    eaterPlayerId = preyPlayerId,
                    eaterCellId = preyCell.id,
                    victimPlayerId = predatorPlayerId,
                    victimCellId = predatorCell.id,
                    massGained = predatorCell.mass
                )
            }
        }

        return PredationDecision.NoCollision
    }
}
